/**
 * Custom SSO/Identity Icons Registry
 *
 * Provides SSO and identity provider icons from local SVG assets.
 * Uses explicit imports with SVGR for optimal tree-shaking.
 */

import type { ComponentType, ReactNode } from 'react';

// Explicit SVG imports with SVGR
import CyberArk from '@/sections/attacks/attackPaths/components/icons/bundles/custom/cyberark.svg?react';
import ForgeRock from '@/sections/attacks/attackPaths/components/icons/bundles/custom/forgerock.svg?react';
import OneLogin from '@/sections/attacks/attackPaths/components/icons/bundles/custom/onelogin.svg?react';
import PingIdentity from '@/sections/attacks/attackPaths/components/icons/bundles/custom/ping-identity.svg?react';
import type {
  IconDefinition,
  IconRenderer,
  IconRenderProps,
} from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { iconRenderers } from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';

// ============================================================================
// Custom Icons Array (source of truth for this registry)
// ============================================================================

const CUSTOM_ICONS = [
  'PingIdentity',
  'OneLogin',
  'CyberArk',
  'ForgeRock',
] as const;

type CustomIconName = (typeof CUSTOM_ICONS)[number];

// ============================================================================
// Icon Component Type
// ============================================================================

type CustomIconComponent = ComponentType<{
  className?: string;
  width?: string | number;
  height?: string | number;
}>;

// ============================================================================
// Fallback Component
// ============================================================================

const FallbackIcon = ({ className }: { className?: string }) => (
  <span className={className}>?</span>
);

// ============================================================================
// Icon Component Map
// ============================================================================

/**
 * Explicit mapping from CUSTOM_ICONS names to SVGR components.
 */
const customIconComponents: Record<CustomIconName, CustomIconComponent> = {
  PingIdentity: PingIdentity,
  OneLogin: OneLogin,
  CyberArk: CyberArk,
  ForgeRock: ForgeRock,
};

/**
 * Human-readable labels for custom icons.
 */
const customIconLabels: Record<CustomIconName, string> = {
  PingIdentity: 'Ping Identity',
  OneLogin: 'OneLogin',
  CyberArk: 'CyberArk',
  ForgeRock: 'ForgeRock',
};

// ============================================================================
// Custom Renderer Implementation
// ============================================================================

/**
 * Custom/SSO icon renderer for the attack graph icon system.
 */
const customRenderer: IconRenderer = {
  render: (
    name: string,
    { size = 24, className }: IconRenderProps
  ): ReactNode => {
    const IconComponent = customIconComponents[name as CustomIconName];
    if (!IconComponent) return null;
    return <IconComponent className={className} width={size} height={size} />;
  },

  validate: (name: string): boolean => {
    return (CUSTOM_ICONS as readonly string[]).includes(name);
  },

  getIcon: (name: string) => {
    const IconComponent = customIconComponents[name as CustomIconName];
    if (!IconComponent) return null;

    return function WrappedCustomIcon({
      size = 24,
      className,
    }: {
      size?: number;
      className?: string;
    }) {
      return <IconComponent className={className} width={size} height={size} />;
    };
  },

  getAll: (): IconDefinition[] =>
    CUSTOM_ICONS.map(name => {
      const IconComponent = customIconComponents[name];
      return {
        id: `custom:${name}`,
        component: IconComponent ?? FallbackIcon,
        label: customIconLabels[name] ?? name,
        category: 'custom',
      };
    }),
};

// ============================================================================
// Register Renderer
// ============================================================================

iconRenderers.set('custom', customRenderer);

export { customRenderer };
